import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactPaginate from "react-paginate";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import {
  IconFileSpreadsheet,
  IconSearch,
  IconChevronRight,
  IconChevronLeft,
  IconRefresh,
  IconEye,
  IconX,
} from "@tabler/icons-react";
import {
  Badge,
  Box,
  Button,
  Fieldset,
  Flex,
  Grid,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Table,
  TableThead,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { boardDateFormat, setActionLog } from "../utils/util";
import { useDynamicScroll } from "../utils/useDynamicScroll";
import { ROW_HOVER_BG } from "../constants/colors";
import { searchActionProps, SHOW_ALIAS } from "../constants/uiActions";

export const formatMacDisplay = (mac?: string | null): string => {
  if (!mac) return "-";
  if (mac.includes("~")) {
    return mac
      .split("~")
      .map((part) => formatMacDisplay(part.trim()))
      .join(" ~ ");
  }
  const clean = mac.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (clean.length === 12) {
    return `${clean.slice(0, 6)} ${clean.slice(6)}`;
  }
  return mac;
};

export const getColorBadgeStyle = (color?: string | null): { bg: string; color: string; border?: string } => {
  if (!color) return { bg: "#f1f3f5", color: "#495057", border: "1px solid #ced4da" };
  const upper = color.trim().toUpperCase().replace(/[\s_-]/g, "");

  switch (upper) {
    case "WHITE":
      return { bg: "#ffffff", color: "#212529", border: "1px solid #ced4da" };
    case "BLACK":
      return { bg: "#212529", color: "#ffffff", border: "1px solid #212529" };
    case "BLUE":
    case "NAVY":
      return { bg: "#1971c2", color: "#ffffff", border: "1px solid #1971c2" };
    case "RED":
      return { bg: "#e03131", color: "#ffffff", border: "1px solid #e03131" };
    case "GRAY":
    case "GREY":
      return { bg: "#495057", color: "#ffffff", border: "1px solid #495057" };
    case "DARKGRAY":
    case "DARKGREY":
      return { bg: "#343a40", color: "#ffffff", border: "1px solid #343a40" };
    case "SILVER":
      return { bg: "#ced4da", color: "#212529", border: "1px solid #adb5bd" };
    case "GOLD":
      return { bg: "#f59f00", color: "#ffffff", border: "1px solid #f59f00" };
    case "YELLOW":
      return { bg: "#ffd43b", color: "#212529", border: "1px solid #fcc419" };
    case "GREEN":
      return { bg: "#2f9e44", color: "#ffffff", border: "1px solid #2f9e44" };
    default:
      return { bg: "#e9ecef", color: "#212529", border: "1px solid #ced4da" };
  }
};

export const formatSecurityKeyDisplay = (key?: string | null): string => {
  if (!key) return "-";
  const trimmed = key.trim();
  return trimmed || "-";
};

export interface TerminalRegistrationHistoryItem {
  terminalRegistrationId?: number;
  groupId: string;
  createdDate: string;
  createdByName?: string;
  totalCount: number;
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  vanType: number;
  vanName: string;
  terminalColor: string;
  aliasName: string;
  minSpSerialNo: string;
  maxSpSerialNo: string;
  spSerialNoPrefix: string;
  spSerialNoDigitLength: number;
  spSerialNoStartNumber: number;
  spSerialNoEndNumber: number;
  spSerialNoRange: string;
  serialNoReverseYn?: string;
  minMacAddress: string;
  maxMacAddress: string;
  macPrefix: string;
  macRange: string;
  mac2StartNumber?: number;
  mac2EndNumber?: number;
  macAddressReverseYn?: string;
  dikYn: string;
  dikSerialStart: string;
  dikSerialEnd: string;
  dikRange: string;
}

export const getSpSerialNumRange = (item: TerminalRegistrationHistoryItem): string => {
  const prefix = item.spSerialNoPrefix || "";
  if (
    item.spSerialNoStartNumber !== undefined &&
    item.spSerialNoStartNumber !== null &&
    item.spSerialNoEndNumber !== undefined &&
    item.spSerialNoEndNumber !== null
  ) {
    const len = item.spSerialNoDigitLength || 1;
    const startStr = String(item.spSerialNoStartNumber).padStart(len, "0");
    const endStr = String(item.spSerialNoEndNumber).padStart(len, "0");
    return startStr === endStr ? startStr : `${startStr} ~ ${endStr}`;
  }
  const min = item.minSpSerialNo || "";
  const max = item.maxSpSerialNo || "";
  if (prefix && min.startsWith(prefix) && max.startsWith(prefix)) {
    const startStr = min.slice(prefix.length);
    const endStr = max.slice(prefix.length);
    return startStr === endStr ? startStr : `${startStr} ~ ${endStr}`;
  }
  return item.spSerialNoRange || item.minSpSerialNo || "-";
};

export const getMacHexRange = (item: TerminalRegistrationHistoryItem): string => {
  if (
    item.mac2StartNumber !== undefined &&
    item.mac2StartNumber !== null &&
    item.mac2EndNumber !== undefined &&
    item.mac2EndNumber !== null
  ) {
    const startHex = item.mac2StartNumber.toString(16).toUpperCase().padStart(6, "0");
    const endHex = item.mac2EndNumber.toString(16).toUpperCase().padStart(6, "0");
    return startHex === endHex ? startHex : `${startHex} ~ ${endHex}`;
  }
  const cleanMin = (item.minMacAddress || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  const cleanMax = (item.maxMacAddress || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (cleanMin.length === 12 && cleanMax.length === 12) {
    const startHex = cleanMin.slice(6);
    const endHex = cleanMax.slice(6);
    return startHex === endHex ? startHex : `${startHex} ~ ${endHex}`;
  }
  return "-";
};

export const getMacDecRange = (item: TerminalRegistrationHistoryItem): string => {
  if (
    item.mac2StartNumber !== undefined &&
    item.mac2StartNumber !== null &&
    item.mac2EndNumber !== undefined &&
    item.mac2EndNumber !== null
  ) {
    return item.mac2StartNumber === item.mac2EndNumber
      ? String(item.mac2StartNumber)
      : `${item.mac2StartNumber} ~ ${item.mac2EndNumber}`;
  }
  const cleanMin = (item.minMacAddress || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  const cleanMax = (item.maxMacAddress || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (cleanMin.length === 12 && cleanMax.length === 12) {
    const startDec = parseInt(cleanMin.slice(6), 16);
    const endDec = parseInt(cleanMax.slice(6), 16);
    if (!isNaN(startDec) && !isNaN(endDec)) {
      return startDec === endDec ? String(startDec) : `${startDec} ~ ${endDec}`;
    }
  }
  return "-";
};

const TerminalAddHistory: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const [data, setData] = useState<TerminalRegistrationHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [length, setLength] = useState(20);
  const [pageNo, setPageNo] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);
  const [isSearch, setIsSearch] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [downloadingGroupId, setDownloadingGroupId] = useState<string | null>(null);

  // Filter states
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [terminalModelId, setTerminalModelId] = useState<string>("");
  const [vanType, setVanType] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");

  // Options
  const [terminalModelOptions, setTerminalModelOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] =
    useState<TerminalRegistrationHistoryItem | null>(null);

  const _isSearch = useRef(false);
  const theme = useMantineTheme();
  useDynamicScroll({ minHeight: 500 });

  // Initial lookup options
  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/model/list", {
        params: { currentPageNo: 1, recordsPerPage: 1000 },
      })
      .then((res) => {
        const list = res.data?.data?.list ?? [];
        setTerminalModelOptions([
          { value: "", label: "전체 모델" },
          ...list.map((m: any) => ({
            value: String(m.terminalModelId),
            label: `${m.internalModelName} / ${m.externalModelName}`,
          })),
        ]);
      })
      .catch((err) => console.error(err));

    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const list = res.data?.data ?? [];
        setVanTypeOptions([
          { value: "", label: "전체 VAN" },
          ...list.map((v: any) => ({
            value: String(v.code),
            label: v.name,
          })),
        ]);
      })
      .catch((err) => console.error(err));
  }, []);

  const getList = () => {
    if (_isSearch.current) return;
    _isSearch.current = true;
    setIsSearch(true);

    const params: any = {
      currentPageNo: pageNo,
      recordsPerPage: length,
    };
    if (startDate) params.startDate = `${startDate} 00:00:00`;
    if (endDate) params.endDate = `${endDate} 23:59:59`;
    if (terminalModelId) params.terminalModelId = Number(terminalModelId);
    if (vanType) params.vanType = Number(vanType);
    if (keyword.trim()) params.keyword = keyword.trim();

    axiosInstance
      .get("/api/v1/terminal/registration-history", { params })
      .then((res) => {
        if (res.data?.data) {
          const list = res.data.data.list ?? [];
          setData(list);
          const paging = res.data.data.paging;
          if (paging) {
            setTotal(paging.totalRecordCount);
            setPageTotal(paging.totalPageCount || 1);
          }
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("등록 이력 조회 중 오류가 발생했습니다.");
      })
      .finally(() => {
        setIsSearch(false);
        _isSearch.current = false;
      });
  };

  useEffect(() => {
    getList();
  }, [pageNo, length]);

  const handleSearch = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 등록 이력 검색 버튼 클릭",
    });
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      getList();
    }
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setTerminalModelId("");
    setVanType("");
    setKeyword("");
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      setTimeout(() => getList(), 50);
    }
  };

  // 상세 보기 핸들러
  const handleOpenDetail = (item: TerminalRegistrationHistoryItem) => {
    setSelectedGroup(item);
    setDetailModalOpen(true);
  };

  // 엑셀 다운로드 핸들러
  const handleExcelDownload = async (
    item: TerminalRegistrationHistoryItem,
    e?: React.MouseEvent
  ) => {
    if (e) e.stopPropagation();
    if (downloadingGroupId) return;

    setDownloadingGroupId(item.groupId);
    await setActionLog({
      eventType: "EXPORT",
      title: `단말기 등록 이력 엑셀 다운로드 (그룹: ${item.groupId})`,
    });

    try {
      const res = await axiosInstance.get(
        `/api/v1/terminal/registration-history/${item.groupId}/terminals`
      );
      const terminals: any[] = res.data?.data ?? [];
      if (terminals.length === 0) {
        toast.warn("다운로드할 단말기 데이터가 없습니다.");
        return;
      }

      const hasDik =
        item.dikYn === "Y" ||
        terminals.some((t) => Boolean(t.securityKeyRef1));

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1");

      const columns: Partial<ExcelJS.Column>[] = [
        { header: "번호", key: "number", width: 8 },
        { header: "S/N", key: "spSerialNo", width: 18 },
        { header: "MAC", key: "macAddress", width: 18 },
        { header: "MAC-2", key: "mac2", width: 10 },
      ];
      if (hasDik) {
        columns.push(
          { header: "보안키", key: "securityKeyRef1", width: 20 },
          { header: "보안키2", key: "securityKeyRef2", width: 36 }
        );
      }
      columns.push(
        { header: "내부모델", key: "internalModelName", width: 14 },
        { header: "외부모델", key: "externalModelName", width: 14 },
        { header: "VAN", key: "vanName", width: 12 },
        { header: "색상", key: "terminalColor", width: 10 }
      );
      worksheet.columns = columns;

      // 헤더 스타일링
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "76933C" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });

      const colCount = columns.length;

      terminals.forEach((t, i) => {
        const rawMac = (t.macAddress || "").replace(/[^0-9A-Fa-f]/g, "");
        const mac2Hex = rawMac.substring(6);
        const mac2Number = mac2Hex ? parseInt(mac2Hex, 16) : "";

        const rowValues: Record<string, string | number> = {
          number: i + 1,
          spSerialNo: t.spSerialNo || "",
          macAddress: rawMac,
          mac2: isNaN(Number(mac2Number)) ? "" : mac2Number,
          internalModelName: t.internalModelName || item.internalModelName || "",
          externalModelName: t.externalModelName || item.externalModelName || "",
          vanName: item.vanName || String(t.vanType || ""),
          terminalColor: t.terminalColor || item.terminalColor || "",
        };

        if (hasDik) {
          rowValues.securityKeyRef1 = t.securityKeyRef1 || "";
          rowValues.securityKeyRef2 = t.securityKeyRef2 || "";
        }

        const dataRow = worksheet.addRow(rowValues);

        const isStripedRow = i % 2 === 0;
        for (let col = 1; col <= colCount; col++) {
          const cell = dataRow.getCell(col);
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin", color: { argb: "DDEBF7" } },
            left: { style: "thin", color: { argb: "DDEBF7" } },
            bottom: { style: "thin", color: { argb: "DDEBF7" } },
            right: { style: "thin", color: { argb: "DDEBF7" } },
          };
          if (isStripedRow) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFEBF1DE" },
            };
          }
        }
      });

      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const cleanAlias = (item.aliasName || "").replace(/[\\/:*?"<>|]/g, "_").trim();
      const modelName = (item.externalModelName || item.internalModelName || "단말기").replace(/[\\/:*?"<>|]/g, "_").trim();
      const count = terminals.length;
      const filename = cleanAlias
        ? `단말기등록_${cleanAlias}_${modelName}_${count}대_${timestamp}.xlsx`
        : `단말기등록_${modelName}_${count}대_${timestamp}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("엑셀 파일 다운로드가 완료되었습니다.");
    } catch (err) {
      console.error(err);
      toast.error("엑셀 파일 생성 중 오류가 발생했습니다.");
    } finally {
      setDownloadingGroupId(null);
    }
  };


  return (
    <Box p="md" style={{ position: "relative", minHeight: "100%" }}>
      <LoadingOverlay visible={isSearch} overlayProps={{ blur: 1 }} />

      {/* 헤더 타이틀 및 검색 영역 */}
      <Flex direction="column" gap="sm" mb="md">
        <Flex justify="space-between" align="center">
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "#2b2f38" }}>
            {menuTitle || "단말기 등록 이력"}
          </h2>
          <Flex align="center" gap="xs">
            <span style={{ fontSize: "0.85rem", color: "#666" }}>
              총 <b style={{ color: theme.colors.blue[7] }}>{total.toLocaleString()}</b> 건
            </span>
            <Select
              size="xs"
              data={[
                { value: "10", label: "10개씩" },
                { value: "20", label: "20개씩" },
                { value: "50", label: "50개씩" },
                { value: "100", label: "100개씩" },
              ]}
              value={String(length)}
              onChange={(val) => {
                setLength(Number(val) || 20);
                setPageNo(1);
              }}
              style={{ width: 95 }}
            />
          </Flex>
        </Flex>

        {/* 필터 바 */}
        <Flex
          wrap="wrap"
          gap="xs"
          align="flex-end"
          p="sm"
          style={{
            backgroundColor: "#f8f9fa",
            borderRadius: 8,
            border: "1px solid #e9ecef",
          }}
        >
          <DateInput
            size="xs"
            label="등록 시작일"
            placeholder="YYYY-MM-DD"
            value={startDate ? new Date(startDate) : null}
            onChange={(d) => setStartDate(d ? dayjs(d).format("YYYY-MM-DD") : "")}
            valueFormat="YYYY-MM-DD"
            clearable
            style={{ width: 130 }}
          />
          <DateInput
            size="xs"
            label="등록 종료일"
            placeholder="YYYY-MM-DD"
            value={endDate ? new Date(endDate) : null}
            onChange={(d) => setEndDate(d ? dayjs(d).format("YYYY-MM-DD") : "")}
            valueFormat="YYYY-MM-DD"
            clearable
            style={{ width: 130 }}
          />
          <Select
            size="xs"
            label="단말기 모델"
            placeholder="전체 모델"
            data={terminalModelOptions}
            value={terminalModelId}
            onChange={(val) => setTerminalModelId(val || "")}
            clearable
            searchable
            style={{ width: 180 }}
          />
          <Select
            size="xs"
            label="VAN사"
            placeholder="전체 VAN"
            data={vanTypeOptions}
            value={vanType}
            onChange={(val) => setVanType(val || "")}
            clearable
            searchable
            style={{ width: 120 }}
          />
          <TextInput
            size="xs"
            label="검색어"
            placeholder={SHOW_ALIAS ? "등록자 / S/N / 별칭 / 모델" : "등록자 / S/N / 모델"}
            value={keyword}
            onChange={(e) => setKeyword(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 190 }}
          />

          <Flex gap="xs" mt="auto">
            <Button
              {...searchActionProps}
              size="xs"
              leftSection={<IconSearch size={14} />}
              onClick={handleSearch}
            >
              조회
            </Button>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={handleReset}
            >
              초기화
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {/* 등록 이력 데이터 테이블 */}
      <ScrollArea style={{ borderRadius: 6, border: "1px solid #dee2e6" }}>
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          verticalSpacing="sm"
          horizontalSpacing="sm"
          style={{ minWidth: 1200 }}
        >
          <TableThead style={{ backgroundColor: "#f1f3f5" }}>
            <Table.Tr>
              <Table.Th style={{ textAlign: "center", width: 50 }}>No</Table.Th>
              <Table.Th style={{ textAlign: "center", width: 110 }}>등록일시</Table.Th>
              <Table.Th style={{ textAlign: "center", width: 100 }}>등록자</Table.Th>
              <Table.Th style={{ textAlign: "left", width: 160 }}>단말기 모델</Table.Th>
              <Table.Th style={{ textAlign: "center", width: 120, whiteSpace: "nowrap" }}>VAN / 색상</Table.Th>
              {SHOW_ALIAS && <Table.Th style={{ textAlign: "center", width: 110 }}>별칭</Table.Th>}
              <Table.Th style={{ textAlign: "center", width: 80 }}>수량</Table.Th>
              <Table.Th style={{ textAlign: "left", width: 180 }}>S/N 범위</Table.Th>
              <Table.Th style={{ textAlign: "left", width: 200 }}>MAC 범위</Table.Th>
              <Table.Th style={{ textAlign: "center", width: 110 }}>DIK 범위</Table.Th>
              <Table.Th style={{ textAlign: "center", width: 90 }}>결과 엑셀</Table.Th>
            </Table.Tr>
          </TableThead>
          <Table.Tbody>
            {data.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={SHOW_ALIAS ? 11 : 10} style={{ textAlign: "center", padding: 40, color: "#888" }}>
                  조회된 등록 이력이 없습니다.
                </Table.Td>
              </Table.Tr>
            ) : (
              data.map((item, index) => {
                const isHovered = hoveredRow === index;
                const rowNo = (pageNo - 1) * length + index + 1;
                const isDownloading = downloadingGroupId === item.groupId;

                return (
                  <Table.Tr
                    key={`${item.terminalRegistrationId || item.groupId}_${item.terminalModelId || ''}_${index}`}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => handleOpenDetail(item)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isHovered ? ROW_HOVER_BG : undefined,
                    }}
                  >
                    <Table.Td style={{ textAlign: "center", color: "#666" }}>{rowNo}</Table.Td>
                    <Table.Td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      {item.createdDate ? (
                        <Flex direction="column" align="center" gap={2}>
                          <div>{dayjs(item.createdDate).format("YYYY-MM-DD")}</div>
                          <div>{dayjs(item.createdDate).format("HH:mm:ss")}</div>
                        </Flex>
                      ) : (
                        "-"
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>
                      {item.createdByName || "-"}
                    </Table.Td>
                    <Table.Td>
                      <Flex direction="column" gap={2}>
                        <div style={{ fontWeight: 600 }}>{item.externalModelName || "-"}</div>
                        <div style={{ color: "#666" }}>{item.internalModelName || "-"}</div>
                      </Flex>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <Flex direction="column" align="center" gap={4}>
                        <div>{item.vanName || item.vanType || "-"}</div>
                        <div>{item.terminalColor || "-"}</div>
                      </Flex>
                    </Table.Td>
                    {SHOW_ALIAS && (
                      <Table.Td style={{ textAlign: "center" }}>
                        {item.aliasName || "-"}
                      </Table.Td>
                    )}
                    <Table.Td style={{ textAlign: "center" }}>
                      {item.totalCount.toLocaleString()}대
                    </Table.Td>
                    <Table.Td>
                      <Flex direction="column" gap={2}>
                        <div>
                          고정: {item.spSerialNoPrefix || "-"}{item.spSerialNoDigitLength ? ` (${item.spSerialNoDigitLength}자리)` : ""}
                        </div>
                        <div>
                          {getSpSerialNumRange(item)}
                          {item.serialNoReverseYn ? (
                            <span style={{ color: item.serialNoReverseYn === "Y" ? "#e03131" : "#1971c2", marginLeft: 4 }}>
                              {item.serialNoReverseYn === "Y" ? "(역순)" : "(정순)"}
                            </span>
                          ) : null}
                        </div>
                      </Flex>
                    </Table.Td>
                    <Table.Td>
                      <Flex direction="column" gap={2}>
                        <div>MAC-1: {item.macPrefix || "-"}</div>
                        <div>{getMacHexRange(item)}</div>
                        <div>
                          {getMacDecRange(item)}
                          {item.macAddressReverseYn ? (
                            <span style={{ color: item.macAddressReverseYn === "Y" ? "#e03131" : "#1971c2", marginLeft: 4 }}>
                              {item.macAddressReverseYn === "Y" ? "(역순)" : "(정순)"}
                            </span>
                          ) : null}
                        </div>
                      </Flex>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>
                      {item.dikYn === "Y" && item.dikRange && item.dikRange !== "-" ? item.dikRange : "-"}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "center" }}>
                      <Button
                        size="xs"
                        variant="light"
                        color="green"
                        loading={isDownloading}
                        leftSection={<IconFileSpreadsheet size={14} />}
                        onClick={(e) => handleExcelDownload(item, e)}
                        style={{ fontWeight: 600 }}
                      >
                        엑셀
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* 페이징 */}
      <Flex justify="center" mt="md">
        <ReactPaginate
          previousLabel={<IconChevronLeft size={16} />}
          nextLabel={<IconChevronRight size={16} />}
          pageCount={pageTotal}
          onPageChange={({ selected }) => setPageNo(selected + 1)}
          forcePage={pageNo - 1}
          containerClassName="pagination"
          activeClassName="active"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakLabel="..."
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
      </Flex>

      {/* 상세 단말기 목록 모달 */}
      <Modal
        opened={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={
          <Flex align="center" justify="space-between" style={{ width: "100%", paddingRight: 20 }}>
            <Flex align="center" gap="xs">
              <IconEye size={18} color="#2563eb" />
              <span style={{ fontWeight: 700, fontSize: "1rem" }}>
                단말기 등록 상세 정보 ({selectedGroup?.externalModelName} / {selectedGroup?.totalCount?.toLocaleString()}대)
              </span>
            </Flex>
            {selectedGroup && (
              <Button
                size="xs"
                variant="filled"
                color="green"
                loading={downloadingGroupId === selectedGroup.groupId}
                leftSection={<IconFileSpreadsheet size={14} />}
                onClick={() => handleExcelDownload(selectedGroup)}
              >
                전체 엑셀 다운로드
              </Button>
            )}
          </Flex>
        }
        size="xl"
        centered
      >
        {selectedGroup && (
          <Box mb="md">
            {/* 기본 정보 */}
            <Box
              mb="sm"
              p="md"
              style={{
                backgroundColor: "#f8fafc",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <Grid gutter="md">
                {/* 1행: 모델, VAN, 색상, 생산 대수 */}
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Flex direction="column" gap={2}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>모델</span>
                    <span style={{ fontSize: "0.95rem", color: "#1e293b", fontWeight: 600 }}>
                      {selectedGroup.externalModelName || "-"}
                      {selectedGroup.internalModelName && (
                        <span style={{ color: "#64748b", fontWeight: 400, marginLeft: 6 }}>
                          ({selectedGroup.internalModelName})
                        </span>
                      )}
                    </span>
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 4, sm: 2.5 }}>
                  <Flex direction="column" gap={2}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>VAN</span>
                    <span style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                      {selectedGroup.vanName || selectedGroup.vanType || "-"}
                    </span>
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 4, sm: 2.5 }}>
                  <Flex direction="column" gap={2}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>색상</span>
                    <span style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                      {selectedGroup.terminalColor || "-"}
                    </span>
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 4, sm: 3 }}>
                  <Flex direction="column" gap={2}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>생산 대수</span>
                    <span style={{ fontSize: "0.95rem", color: "#1e293b", fontWeight: 600 }}>
                      {selectedGroup.totalCount?.toLocaleString()}대
                    </span>
                  </Flex>
                </Grid.Col>

                {/* 2행: 별칭 (SHOW_ALIAS 활성화 시 표시), 등록자, 등록일시 */}
                {SHOW_ALIAS && (
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Flex direction="column" gap={2}>
                      <span style={{ fontSize: "0.85rem", color: "#64748b" }}>별칭</span>
                      <span style={{ fontSize: "0.95rem", color: "#1e293b", wordBreak: "break-all" }}>
                        {selectedGroup.aliasName || "-"}
                      </span>
                    </Flex>
                  </Grid.Col>
                )}
                <Grid.Col span={{ base: 6, sm: SHOW_ALIAS ? 2.5 : 6 }}>
                  <Flex direction="column" gap={2}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>등록자</span>
                    <span style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                      {selectedGroup.createdByName || "-"}
                    </span>
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: SHOW_ALIAS ? 3.5 : 6 }}>
                  <Flex direction="column" gap={2}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>등록일시</span>
                    <span style={{ fontSize: "0.95rem", color: "#1e293b", whiteSpace: "nowrap" }}>
                      {boardDateFormat(selectedGroup.createdDate)}
                    </span>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>

            {/* S/N 및 MAC 설정 (직접입력 UI 형식) */}
            <Grid gutter="xs" mb="xs">
              {/* S/N Fieldset */}
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Fieldset
                  legend="S/N"
                  styles={{
                    legend: { fontWeight: 600, fontSize: "0.9rem", color: "#1e293b" },
                    root: { padding: "10px 14px", border: "1px solid #cbd5e1", borderRadius: 8 },
                  }}
                >
                  <Grid gutter="xs" mb="xs">
                    <Grid.Col span={6}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>고정문자열</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.spSerialNoPrefix || "-"}
                      </div>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>자릿수</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.spSerialNoDigitLength ? `${selectedGroup.spSerialNoDigitLength}자리` : "-"}
                      </div>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>시작번호</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.spSerialNoStartNumber !== undefined && selectedGroup.spSerialNoStartNumber !== null
                          ? selectedGroup.spSerialNoStartNumber
                          : "-"}
                      </div>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>종료번호</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.spSerialNoEndNumber !== undefined && selectedGroup.spSerialNoEndNumber !== null
                          ? selectedGroup.spSerialNoEndNumber
                          : "-"}
                      </div>
                    </Grid.Col>
                  </Grid>
                  <Box p="xs" style={{ backgroundColor: "#f1f5f9", borderRadius: 6, fontSize: "0.9rem" }}>
                    <Flex justify="space-between" mb={3}>
                      <span style={{ color: "#64748b" }}>시작 S/N</span>
                      <span style={{ color: "#1e293b" }}>{selectedGroup.minSpSerialNo || "-"}</span>
                    </Flex>
                    <Flex justify="space-between" mb={3}>
                      <span style={{ color: "#64748b" }}>종료 S/N</span>
                      <span style={{ color: "#1e293b" }}>{selectedGroup.maxSpSerialNo || "-"}</span>
                    </Flex>
                    <Flex justify="space-between" mb={3}>
                      <span style={{ color: "#64748b" }}>등록 순서</span>
                      <span style={{ color: selectedGroup.serialNoReverseYn === "Y" ? "#e03131" : "#1971c2", fontWeight: 500 }}>
                        {selectedGroup.serialNoReverseYn === "Y" ? "역순 (내림차순)" : "정순 (오름차순)"}
                      </span>
                    </Flex>
                    <Flex justify="space-between">
                      <span style={{ color: "#64748b" }}>총 생산 대수</span>
                      <span style={{ color: "#1e293b", fontWeight: 600 }}>
                        {selectedGroup.totalCount?.toLocaleString()}대
                      </span>
                    </Flex>
                  </Box>
                </Fieldset>
              </Grid.Col>

              {/* MAC address Fieldset */}
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Fieldset
                  legend="MAC address"
                  styles={{
                    legend: { fontWeight: 600, fontSize: "0.9rem", color: "#1e293b" },
                    root: { padding: "10px 14px", border: "1px solid #cbd5e1", borderRadius: 8 },
                  }}
                >
                  <Grid gutter="xs" mb="xs">
                    <Grid.Col span={12}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>MAC-1 (고정문자열)</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.macPrefix || (selectedGroup.minMacAddress ? selectedGroup.minMacAddress.substring(0, 6) : "-")}
                      </div>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>MAC-2 시작번호</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.mac2StartNumber !== undefined && selectedGroup.mac2StartNumber !== null
                          ? selectedGroup.mac2StartNumber
                          : (selectedGroup.minMacAddress && selectedGroup.minMacAddress.length >= 12
                              ? parseInt(selectedGroup.minMacAddress.slice(6), 16)
                              : "-")}
                      </div>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <div style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 2 }}>MAC-2 종료번호</div>
                      <div style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                        {selectedGroup.mac2EndNumber !== undefined && selectedGroup.mac2EndNumber !== null
                          ? selectedGroup.mac2EndNumber
                          : (selectedGroup.maxMacAddress && selectedGroup.maxMacAddress.length >= 12
                              ? parseInt(selectedGroup.maxMacAddress.slice(6), 16)
                              : "-")}
                      </div>
                    </Grid.Col>
                  </Grid>
                  <Box p="xs" style={{ backgroundColor: "#f1f5f9", borderRadius: 6, fontSize: "0.9rem" }}>
                    <Flex justify="space-between" mb={3}>
                      <span style={{ color: "#64748b" }}>시작 MAC</span>
                      <span style={{ color: "#1e293b" }}>
                        {formatMacDisplay(selectedGroup.minMacAddress)}
                        {selectedGroup.mac2StartNumber !== undefined && selectedGroup.mac2StartNumber !== null
                          ? ` (${selectedGroup.mac2StartNumber})`
                          : (selectedGroup.minMacAddress && selectedGroup.minMacAddress.length >= 12
                              ? ` (${parseInt(selectedGroup.minMacAddress.slice(6), 16)})`
                              : "")}
                      </span>
                    </Flex>
                    <Flex justify="space-between" mb={3}>
                      <span style={{ color: "#64748b" }}>종료 MAC</span>
                      <span style={{ color: "#1e293b" }}>
                        {formatMacDisplay(selectedGroup.maxMacAddress)}
                        {selectedGroup.mac2EndNumber !== undefined && selectedGroup.mac2EndNumber !== null
                          ? ` (${selectedGroup.mac2EndNumber})`
                          : (selectedGroup.maxMacAddress && selectedGroup.maxMacAddress.length >= 12
                              ? ` (${parseInt(selectedGroup.maxMacAddress.slice(6), 16)})`
                              : "")}
                      </span>
                    </Flex>
                    <Flex justify="space-between" mb={3}>
                      <span style={{ color: "#64748b" }}>등록 순서</span>
                      <span style={{ color: selectedGroup.macAddressReverseYn === "Y" ? "#e03131" : "#1971c2", fontWeight: 500 }}>
                        {selectedGroup.macAddressReverseYn === "Y" ? "역순 (내림차순)" : "정순 (오름차순)"}
                      </span>
                    </Flex>
                    <Flex justify="space-between">
                      <span style={{ color: "#64748b" }}>총 MAC 수량</span>
                      <span style={{ color: "#1e293b", fontWeight: 600 }}>
                        {selectedGroup.totalCount?.toLocaleString()}대
                      </span>
                    </Flex>
                  </Box>
                </Fieldset>
              </Grid.Col>
            </Grid>

            {/* DIK Fieldset (해당 시) */}
            {selectedGroup.dikYn === "Y" && (
              <Fieldset
                legend="DIK"
                mb="xs"
                styles={{
                  legend: { fontWeight: 600, fontSize: "0.9rem", color: "#1e293b" },
                  root: { padding: "10px 14px", border: "1px solid #cbd5e1", borderRadius: 8 },
                }}
              >
                <Flex align="center" justify="space-between" wrap="wrap" gap="sm">
                  <Flex align="center" gap="xs">
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>DIK 적용 여부:</span>
                    <span style={{ fontSize: "0.9rem", color: "#1e293b", fontWeight: 600 }}>포함</span>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>DIK 범위:</span>
                    <span style={{ fontSize: "0.9rem", color: "#1e293b" }}>
                      {selectedGroup.dikRange || (selectedGroup.dikSerialStart && selectedGroup.dikSerialEnd ? `${selectedGroup.dikSerialStart} ~ ${selectedGroup.dikSerialEnd}` : "-")}
                    </span>
                  </Flex>
                </Flex>
              </Fieldset>
            )}
          </Box>
        )}

        <Flex justify="flex-end" mt="md">
          <Button variant="default" size="sm" onClick={() => setDetailModalOpen(false)}>
            닫기
          </Button>
        </Flex>
      </Modal>
    </Box>
  );
};

export default TerminalAddHistory;
